import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';

/**
 * A short "before you claim" checklist for the Get EA page.
 *
 * The page ends in a chat hand-off, so a visitor should be able to confirm they
 * can actually run GoldWarden before starting the conversation. These facts are
 * kept identical to the requirements panel on the How It Works page so the two
 * pages never drift apart.
 *
 * It is deliberately rendered as a single checklist panel rather than a grid of
 * icon cards, so it reads as a different kind of content from the "What you get"
 * cards above it and the two sections do not blur together.
 */
const REQUIREMENTS = [
  { id: 'platform', text: 'A MetaTrader 5 account with XAUUSD available' },
  { id: 'balance', text: 'Around $500 minimum, to size the presets correctly' },
  { id: 'vps', text: 'MetaTrader 5 on Windows or Mac — a Windows VPS keeps it trading 24/5' },
  { id: 'spread', text: 'A raw or low-spread account type for cleaner fills' },
  { id: 'net', text: 'A stable internet connection to your broker' },
  { id: 'account', text: 'Hedging or netting account — both work fine' },
];

export default function Requirements() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Before you claim"
              title="What you need to run it"
              subtitle="GoldWarden is free, but it only works on the right setup. Run through this quick list first — if any line is missing, tell us in the chat and we will point you the right way."
            />
          </Reveal>

          <Reveal delay={100}>
            <div className="mt-10 md:mt-12 mx-auto max-w-4xl rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                {REQUIREMENTS.map((item) => (
                  <li key={item.id} className="flex items-start gap-3">
                    <span className="mt-0.5 shrink-0 w-6 h-6 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className="ri-check-line text-sm" aria-hidden="true"></i>
                    </span>
                    <span className="text-xs md:text-sm leading-relaxed text-foreground-700">{item.text}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-6 pt-5 border-t border-background-200 flex items-start sm:items-center gap-2 text-xs md:text-sm text-foreground-600">
                <i className="ri-information-line text-base text-secondary-600 shrink-0" aria-hidden="true"></i>
                Already running MT5 on a low-spread account? Then you are good to go.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}